import { useEffect, useState } from 'react';
import { Loader2Icon } from 'lucide-react';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';

import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { NbButton, NbCard, NbPageHeader } from '@/components/Neobrut';
import { CAT_RACES, DOG_RACES, PET_AGES_LABELS, PET_COLORS } from '@/constants';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuthToken } from '@/hooks/useAuthToken';
import { ImageUploader } from '@/components/ImageUploader/ImageUploader';
import {
  createOwnedPet,
  updateOwnedPet,
  fetchOwnedPetById,
  uploadOwnedPetImages,
  deleteOwnedPetImage,
} from '@/api/ownedPets';

interface FormValues {
  type: PetType;
  name: string;
  gender: PetGender;
  age: PetAges;
  mainColor: PetColor;
  race: string;
  is_sterilized: boolean;
  is_vaccinated: boolean;
  is_microchipped: boolean;
  microchip_number?: string;
  medical_notes?: string;
  images?: FileList;
}

export const CreatePetProfile = () => {
  const { token, isTokenExpired } = useAuthToken();
  const { id } = useParams();
  const isEditMode = !!id;
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [existingImages, setExistingImages] = useState<{ id: number; url: string }[]>([]);
  const navigate = useNavigate();
  const { register, control, handleSubmit, watch, reset } = useForm<FormValues>({
    defaultValues: {
      type: 'cat',
      age: 'baby',
      gender: 'm',
      name: '',
      race: '',
      mainColor: undefined,
      is_sterilized: false,
      is_vaccinated: false,
      is_microchipped: false,
      microchip_number: '',
      medical_notes: '',
    },
  });

  const type = watch('type');
  const isMicrochipped = watch('is_microchipped');

  useEffect(() => {
    if (!token || isTokenExpired(token)) {
      navigate('/login');
    }
  }, [token, isTokenExpired, navigate]);

  useEffect(() => {
    if (isEditMode && id && token) {
      const loadPet = async () => {
        try {
          const pet = await fetchOwnedPetById(parseInt(id), token);
          reset({
            type: pet.type,
            name: pet.name,
            gender: pet.gender,
            age: pet.age,
            mainColor: pet.main_color,
            race: pet.race,
            is_sterilized: pet.is_sterilized,
            is_vaccinated: pet.is_vaccinated,
            is_microchipped: pet.is_microchipped,
            microchip_number: pet.microchip_number || '',
            medical_notes: pet.medical_notes || '',
          });
          setExistingImages(pet.gallery_items ?? []);
        } catch (error) {
          console.error('Error loading pet:', error);
          toast.error('Error al cargar el perfil de la mascota');
          navigate('/my-pets');
        }
      };
      loadPet();
    }
  }, [isEditMode, id, token, reset, navigate]);

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    try {
      setErrors([]);

      // Validate name
      if (!data.name || data.name.length < 2) {
        setErrors(['Ingrese el nombre de la mascota']);
        return;
      }

      // Validate images for create mode
      if (!isEditMode && (!data.images || data.images.length === 0)) {
        setErrors(['Debes seleccionar al menos una imagen']);
        return;
      }

      if (data.images && data.images.length > 4) {
        setErrors(['Puedes subir un máximo de 4 imágenes']);
        return;
      }

      if (!token) {
        toast.error('No estás autenticado');
        navigate('/login');
        return;
      }

      setIsLoading(true);

      const petData = {
        type: data.type,
        name: data.name,
        gender: data.gender,
        age: data.age,
        main_color: data.mainColor,
        race: data.race,
        is_sterilized: data.is_sterilized,
        is_vaccinated: data.is_vaccinated,
        is_microchipped: data.is_microchipped,
        microchip_number: data.is_microchipped ? data.microchip_number : '',
        medical_notes: data.medical_notes || '',
      };

      let petId: number;

      if (isEditMode && id) {
        // Update existing pet
        await updateOwnedPet(parseInt(id), petData, token);
        petId = parseInt(id);
        toast.success('Perfil actualizado exitosamente');
      } else {
        // Create new pet
        const newPet = await createOwnedPet(petData, token);
        petId = newPet.id;
        toast.success('Perfil creado exitosamente');
      }

      // Upload images if provided
      if (data.images && data.images.length > 0) {
        const imageFiles = Array.from(data.images);
        await uploadOwnedPetImages(petId, imageFiles, token);
      }

      setIsLoading(false);
      navigate('/my-pets');
    } catch (error: any) {
      console.error(error);
      setIsLoading(false);

      if (error.response?.status === 400) {
        const errorData = error.response.data;
        const errorMessages = Object.entries(errorData).map(
          ([key, value]) => `${key}: ${value}`,
        );
        setErrors(errorMessages);
      } else {
        toast.error('Error al guardar el perfil de la mascota');
      }
    }
  };

  return (
    <div className="mx-auto w-full max-w-2xl lg:max-w-3xl pb-10">
      <NbPageHeader
        slug={isEditMode ? '/ editar mascota' : '/ nueva mascota'}
        title={
          isEditMode ? (
            <>
              Actualizar
              <br />
              <span className="nb-highlight">perfil.</span>
            </>
          ) : (
            <>
              Un perfil
              <br />
              <span className="nb-highlight">con QR.</span>
            </>
          )
        }
        subtitle={
          isEditMode
            ? 'Actualizá la info de tu mascota y sus imágenes.'
            : 'Creá un perfil permanente y generá un código QR para su collar o placa identificadora.'
        }
        tone="plain"
      />

      <form onSubmit={handleSubmit(onSubmit)} className="px-4 pt-4">
        <NbCard className="flex flex-col gap-4 p-4">
        {errors.length > 0 && (
          <div className="rounded-lg bg-red-50 border-2 border-red-500 p-4">
            <h3 className="mb-2 font-bold text-red-800">Por favor corrige los siguientes errores:</h3>
            <ul className="list-inside list-disc text-sm text-red-700">
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex flex-col gap-1">
          <label className="font-bold">Tipo:</label>
          <Controller
            name="type"
            control={control}
            render={({ field }) => (
              <RadioGroup
                {...field}
                onValueChange={field.onChange}
                defaultValue={field.value}
                className="flex w-full flex-row gap-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="cat" id="cat" />
                  <Label htmlFor="cat">Gato</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="dog" id="dog" />
                  <Label htmlFor="dog">Perro</Label>
                </div>
              </RadioGroup>
            )}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="name" className="font-bold">Nombre:</label>
          <input
            type="text"
            id="name"
            className="border-2 border-black rounded-md p-2"
            {...register('name')}
            placeholder="Nombre de tu mascota"
            required
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="font-bold">Sexo:</label>
          <Controller
            name="gender"
            control={control}
            render={({ field }) => (
              <RadioGroup
                {...field}
                onValueChange={field.onChange}
                defaultValue={field.value}
                className="flex w-full flex-row gap-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="m" id="male" />
                  <Label htmlFor="male">Macho</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="f" id="female" />
                  <Label htmlFor="female">Hembra</Label>
                </div>
              </RadioGroup>
            )}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="font-bold">Edad:</label>
          <Controller
            name="age"
            control={control}
            render={({ field }) => (
              <Select {...field} onValueChange={field.onChange} defaultValue={field.value}>
                <SelectTrigger className="h-10 w-full bg-white font-medium border-2 border-black">
                  <SelectValue placeholder="Selecciona una edad" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(PET_AGES_LABELS).map(([key, value]) => (
                    <SelectItem key={key} value={key}>
                      {value}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="font-bold">Raza:</label>
          <Controller
            name="race"
            control={control}
            render={({ field }) => (
              <Select {...field} onValueChange={field.onChange} defaultValue={field.value}>
                <SelectTrigger className="h-10 w-full bg-white font-medium border-2 border-black">
                  <SelectValue placeholder="Selecciona una raza" />
                </SelectTrigger>
                <SelectContent>
                  {(type === 'cat' ? CAT_RACES : DOG_RACES).map((race) => (
                    <SelectItem key={race} value={race}>
                      {race}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="mainColor" className="font-bold">Color:</label>
          <Controller
            name="mainColor"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="h-10 w-full bg-white font-medium">
                  <SelectValue placeholder="Seleccionar color" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(PET_COLORS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="is_sterilized" className="font-bold">¿Esterilizado?</label>
          <Controller
            name="is_sterilized"
            control={control}
            render={({ field }) => (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="is_sterilized"
                  className="h-5 w-5"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
                <label htmlFor="is_sterilized" className="text-sm">
                  Marcar si la mascota está esterilizada
                </label>
              </div>
            )}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="is_vaccinated" className="font-bold">¿Vacunado?</label>
          <Controller
            name="is_vaccinated"
            control={control}
            render={({ field }) => (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="is_vaccinated"
                  className="h-5 w-5"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
                <label htmlFor="is_vaccinated" className="text-sm">
                  Marcar si la mascota está vacunada
                </label>
              </div>
            )}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="is_microchipped" className="font-bold">¿Con Microchip?</label>
          <Controller
            name="is_microchipped"
            control={control}
            render={({ field }) => (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="is_microchipped"
                  className="h-5 w-5"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
                <label htmlFor="is_microchipped" className="text-sm">
                  Marcar si la mascota tiene microchip
                </label>
              </div>
            )}
          />
        </div>

        {isMicrochipped && (
          <div className="flex flex-col gap-1">
            <label htmlFor="microchip_number" className="font-bold">Número de Microchip:</label>
            <input
              type="text"
              id="microchip_number"
              className="border-2 border-black rounded-md p-2"
              {...register('microchip_number')}
              placeholder="Número de microchip"
            />
          </div>
        )}

        <div className="flex flex-col gap-1">
          <label htmlFor="medical_notes" className="font-bold">Notas médicas:</label>
          <textarea
            id="medical_notes"
            rows={4}
            placeholder="Alergias, medicamentos, condiciones médicas especiales, etc."
            className="border-2 border-black rounded-md p-2"
            {...register('medical_notes')}
          />
          <p className="text-sm text-gray-600 mt-1">Esta información será visible en el perfil público</p>
        </div>

        <div className="flex flex-col gap-1">
          <Label htmlFor="images" className="font-bold">Imágenes:</Label>
          <p className="text-sm text-gray-600">
            Selecciona hasta 4 imágenes de la mascota. Las imágenes deben ser cuadradas.
          </p>
          <Controller
            name="images"
            control={control}
            render={({ field }) => (
              <ImageUploader
                existingImages={isEditMode ? existingImages : undefined}
                onImagesSelected={(images: File[]) => {
                  field.onChange(images);
                }}
                onRemoveExisting={
                  isEditMode && token
                    ? async (imageId) => {
                        try {
                          await deleteOwnedPetImage(imageId, token);
                          setExistingImages((prev) => prev.filter((i) => i.id !== imageId));
                          toast.success('Imagen eliminada');
                        } catch {
                          toast.error('No se pudo eliminar la imagen');
                        }
                      }
                    : undefined
                }
              />
            )}
          />
        </div>

          <NbButton
            fill="ink"
            type="submit"
            disabled={isLoading}
            className="w-full py-3"
          >
            {isLoading && <Loader2Icon className="h-4 w-4 animate-spin" />}
            {isEditMode ? 'Actualizar perfil' : 'Crear perfil'}
          </NbButton>

          <NbButton
            fill="paper"
            type="button"
            onClick={() => navigate('/my-pets')}
            disabled={isLoading}
            className="w-full py-3"
          >
            Cancelar
          </NbButton>
        </NbCard>
      </form>
    </div>
  );
};
